import Button from '@ui/buttons/button/button';
import Container from '@ui/container/container';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import Text from '@ui/text/text/text';
import DatePicker, { DatePickerProps, ReactDatePickerCustomHeaderProps } from 'react-datepicker';
import { getZerothMinuteOfDayInTimezone } from './incremental-options-utils/get-zeroth-minute-of-day-in-timezone';
import { MILLISECONDS_IN_MINUTE } from './incremental-options-utils/time-constants';
import { getLocalAdjustedDate } from './helpers/get-local-adjusted-date';
import { getTimezoneOffsetForDateMilliseconds } from './incremental-options-utils/get-timezone-offset-milliseconds';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import useGetColorForProjectThemeComponents from '@/utils/colors/use-get-color-for-project-theme-components';
import useWorkingProjectColorsByV2Names from '@/utils/use-working-project-colors-by-v2-names';
import { DateRange } from './types';
import timezoneMap from './timezone-name-map.json' with { type: 'json' };
import { memo, useEffect } from 'react';
import { getLocalAdjustedZerothDate } from './helpers/get-local-adjusted-zeroth-date';

type DateRangeDayPickerProps = {
  /** only pass in value if you want the component to be controlled */
  value?: DateRange;
  onChange?: (data: DateRange) => void;
  datePickerProps?: DatePickerProps;
  selectSingleDate?: boolean;
  startDate: Date | null;
  setStartDate: (startDate: Date | null) => void;
  endDate: Date | null;
  setEndDate: (endDate: Date | null) => void;
  selectedTimezone?: keyof typeof timezoneMap;
  setInvalidEndTime: (valid: boolean) => void;
};

const DateRangeDayPicker = memo(
  ({
    value,
    onChange: propsChange,
    datePickerProps,
    selectSingleDate = false,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    selectedTimezone = 'America/Los_Angeles',
    setInvalidEndTime,
  }: DateRangeDayPickerProps) => {
    const { primary: _primaryColor } = useWorkingProjectColorsByV2Names();
    const primaryColor = _primaryColor && _primaryColor[0];
    const projectThemeColors = useGetColorForProjectThemeComponents({ colorVariable: EColorVariablesV2.buttonText });

    // machine utc date offset for a given date in minutes. Multiplying by -1 returns negative number for machines that are behind UTC and positive for machines ahead of UTC
    const machineOffsetMilliseconds = startDate ? startDate?.getTimezoneOffset() * MILLISECONDS_IN_MINUTE * -1 : 0;

    // get the offset for a given timezone at a given date in minutes
    const zonedOffsetMilliseconds = startDate
      ? getTimezoneOffsetForDateMilliseconds(startDate?.getTime(), selectedTimezone)
      : 0;

    // React date-picker does not take into account timezones, but instead transforms dates to local time
    // We need to trick the UI into displaying dates adjusted for a combination of the selected timezone and the machine time.
    // We do this by subtracting subtracting the zonedOffsetMinues from the machineOffsetMinutes
    const timezoneDiffsMilliseconds = machineOffsetMilliseconds - zonedOffsetMilliseconds;

    // using the offset difference between the machine and the selectedTimezone, adjust the start and end dates so the day picker
    // shows days as if they were selectedTimezone, but they are infact just phony dates that appear as such
    const localAdjustedStart = getLocalAdjustedDate(startDate, timezoneDiffsMilliseconds);
    const localAdjustedEnd = getLocalAdjustedDate(endDate, timezoneDiffsMilliseconds);

    // get the 0th millisecond of the selected date in a given timeozne so we have known values to compare when determining day comparisons
    const zerothStart = startDate ? getZerothMinuteOfDayInTimezone(startDate.getTime(), selectedTimezone) : 0;
    const zerothEnd = endDate ? getZerothMinuteOfDayInTimezone(endDate.getTime(), selectedTimezone) : 0;

    useEffect(() => {
      if (value?.startDate && selectSingleDate) {
        setStartDate(new Date(value.startDate));
      } else if (value?.startDate && value.endDate) {
        setStartDate(new Date(value.startDate));
        setEndDate(new Date(value.endDate));
      }
    }, [value, selectSingleDate, setStartDate, setEndDate, selectedTimezone]);

    const handleDateRangeSelection = (dates: Date[]) => {
      const isDateOrNull = dates.every((date) => date === null || date instanceof Date);
      if (!isDateOrNull) return;

      const [start, end] = dates;

      // startDate and endDate are not true values we want to store since we adjusted and used localAdjustedStart and localAdjustedEnd
      // We need to "un-adjust" for the diff by multiplying the timezoneDiffsMilliseconds by -1 and pass to getLocalAdjustedDate
      // to reset the value to what we are actually interested in.
      const localUnAdjustedStart = getLocalAdjustedDate(start, timezoneDiffsMilliseconds * -1);
      const localUnAdjustedEnd = getLocalAdjustedDate(end, timezoneDiffsMilliseconds * -1);

      const newEndDate = localUnAdjustedEnd ? new Date(localUnAdjustedEnd) : null;
      const startYear = localUnAdjustedStart?.getFullYear() || 0;
      const startMonth = localUnAdjustedStart?.getMonth() || 0;
      const startDay = localUnAdjustedStart?.getDate() || 0;
      const startHour = localUnAdjustedStart?.getHours() || 0;
      const startMinute = localUnAdjustedStart?.getMinutes() || 0;
      const endYear = localUnAdjustedEnd?.getFullYear() || 0;
      const endMonth = localUnAdjustedEnd?.getMonth() || 0;
      const endDay = localUnAdjustedEnd?.getDate() || 0;
      const endHour = localUnAdjustedEnd?.getHours() || 0;
      const endMinute = localUnAdjustedEnd?.getMinutes() || 0;

      if (
        !value?.endDate &&
        newEndDate &&
        startHour === endHour &&
        startMinute === endMinute &&
        startYear === endYear &&
        startMonth === endMonth &&
        startDay === endDay
      ) {
        // add 5 minutes to the end date
        newEndDate.setMinutes(newEndDate.getMinutes() + 5);
      }

      setStartDate(localUnAdjustedStart);
      setEndDate(newEndDate);

      setInvalidEndTime(!!(newEndDate && localUnAdjustedStart && newEndDate < localUnAdjustedStart));

      propsChange?.({
        startDate: localUnAdjustedStart?.getTime() || null,
        endDate: newEndDate?.getTime() || null,
        selectedTimezone,
      });
    };

    const handleSingleDateSelection = (date: Date | null) => {
      if (!date) return;

      const localUnAdjustedEnd = getLocalAdjustedDate(date, timezoneDiffsMilliseconds * -1);

      setStartDate(localUnAdjustedEnd);

      propsChange?.({
        startDate: localUnAdjustedEnd?.getTime() || null,
        endDate: null,
        selectedTimezone,
      });
    };

    return (
      <DatePicker
        // this spread is technically correct, but react-datepicker has a poorly written type definition when it comes to user accessibility
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        {...(datePickerProps as any)}
        selected={localAdjustedStart}
        calendarClassName={`custom-date-picker-calendar ${datePickerProps?.calendarClassName || ''}`}
        renderCustomHeader={(headerProps: ReactDatePickerCustomHeaderProps) => {
          return (
            <div className="flex fd-row jc-center ai-center width-100-percent height-32 margin-b-8 padding-h-4">
              <div>
                <Button
                  className="clear-button square-32 flex fd-row jc-center ai-center"
                  onClick={headerProps.decreaseMonth}
                >
                  <Icon name={ICONS.KEYBOARD_ARROW_LEFT} size={12} />
                </Button>
              </div>
              {/* render short month name from Date */}
              <div className="flex-1 S1">
                <Text color="var(--gray-100)" className="text-center">
                  {headerProps.monthDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
                </Text>
              </div>
              <div>
                <Button
                  className="clear-button square-32 flex fd-row jc-center ai-center"
                  onClick={headerProps.increaseMonth}
                >
                  <Icon name={ICONS.KEYBOARD_ARROW_RIGHT} size={12} />
                </Button>
              </div>
            </div>
          );
        }}
        renderDayContents={(day, date) => {
          // date is the value of each of the buttons on the calendar
          // adjsut the picker date to for local display
          const zerothDate = getLocalAdjustedZerothDate(
            date,
            zonedOffsetMilliseconds,
            selectedTimezone,
            machineOffsetMilliseconds
          );
          const isStart = startDate && zerothDate === zerothStart;
          const isEnd = endDate && zerothDate === zerothEnd;

          const selectedProps =
            isStart || isEnd
              ? {
                  style: { color: projectThemeColors?.hex },
                  gradientOverlay: {
                    height: '100%',
                    background: `${primaryColor}`,
                  },
                }
              : {};
          const singleDay = zerothStart && zerothEnd && zerothStart === zerothEnd;

          return (
            <Container
              width="32px"
              height="32px"
              background="var(--gray-800)"
              {...selectedProps}
              borderRadius={isStart || isEnd ? '100%' : '0'}
              className={`P2 custom-date-picker-day-content-wrapper overflow-hidden ${singleDay ? 'custom-date-picker-single-day' : ''} ${isStart ? 'custom-date-picker-start-date' : ''} ${isEnd ? 'custom-date-picker-end-date' : ''}`}
            >
              <div className="position-relative height-100-percent width-100-percent flex fd-row jc-center ai-center">
                {day}
              </div>
            </Container>
          );
        }}
        onChange={selectSingleDate ? handleSingleDateSelection : handleDateRangeSelection}
        startDate={localAdjustedStart}
        endDate={localAdjustedEnd}
        selectsRange={!selectSingleDate}
        selectsDisabledDaysInRange={datePickerProps?.selectsDisabledDaysInRange ?? false}
        inline={datePickerProps?.inline ?? true}
      />
    );
  }
);

export default DateRangeDayPicker;
